import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Truck, ScrollText, Printer, CheckCircle, ClipboardList } from 'lucide-react';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const ProjectRestock = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [goToPageInput, setGoToPageInput] = useState('');
  const [printColumns, setPrintColumns] = useState({
    code_produit: true,
    categorie: false,
    stock_actuel: true,
    stock_minimum: true,
    prix_achat: true,
    quantite: true,
    cout_total: true,
    checkbox: true
  });
  const printRef = useRef();

  useEffect(() => {
    loadRestockList();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadSettings = async () => {
    try {
      const res = await api.get('/business/settings');
      setSettings(res.data);
    } catch (err) {
      console.error('Erreur chargement paramètres:', err);
    }
  };

  const loadRestockList = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/workspace/restock-list`);
      setData(res.data);
      
      const initial = {};
      res.data.products?.forEach(p => {
        initial[p.id] = {
          selected: p.is_critical === 1 && p.stock_minimum > 0, // Coché si critique ET seuil > 0
          quantity: p.quantite_a_commander
        };
      });
      setSelectedProducts(initial);
    } catch (err) {
      console.error('Erreur chargement liste ravitaillement:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        selected: !prev[productId]?.selected
      }
    }));
  };

  const updateQuantity = (productId, quantity) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity: Math.max(0, parseInt(quantity) || 0)
      }
    }));
  };

  const selectAll = () => {
    const updated = {};
    data?.products?.forEach(p => {
      updated[p.id] = {
        selected: true,
        quantity: selectedProducts[p.id]?.quantity || p.quantite_a_commander
      };
    });
    setSelectedProducts(updated);
  };

  const deselectAll = () => {
    const updated = {};
    data?.products?.forEach(p => {
      updated[p.id] = {
        selected: false,
        quantity: selectedProducts[p.id]?.quantity || p.quantite_a_commander
      };
    });
    setSelectedProducts(updated);
  };

  const getSelectedSummary = () => {
    let totalProducts = 0;
    let totalUnits = 0;
    let totalCost = 0;

    data?.products?.forEach(p => {
      if (selectedProducts[p.id]?.selected) {
        totalProducts++;
        const qty = selectedProducts[p.id]?.quantity || 0;
        totalUnits += qty;
        totalCost += qty * (p.prix_achat || 0);
      }
    });

    return { totalProducts, totalUnits, totalCost };
  };

  // Filtrer les produits selon la recherche et l'affichage sélectionnés
  const getFilteredProducts = () => {
    let products = data?.products || [];
    
    // Filtrer par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      products = products.filter(p => 
        p.nom.toLowerCase().includes(query) ||
        (p.code_produit && p.code_produit.toLowerCase().includes(query)) ||
        (p.categorie && p.categorie.toLowerCase().includes(query))
      );
    }
    
    // Filtrer par sélection
    if (showOnlySelected) {
      products = products.filter(p => selectedProducts[p.id]?.selected);
    }
    
    return products;
  };

  // Obtenir les produits paginés
  const getPaginatedProducts = () => {
    const filtered = getFilteredProducts();
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    
    return {
      products: filtered.slice(start, end),
      totalPages,
      totalItems: filtered.length,
      currentPage
    };
  };

  // Générer les numéros de page à afficher
  const getPaginationNumbers = () => {
    const { totalPages } = getPaginatedProducts();
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, currentPage - half);
      let end = Math.min(totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      if (start > 1) pages.push(1, '...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages) pages.push('...', totalPages);
    }
    
    return pages;
  };

  const handleGoToPage = (page) => {
    const num = parseInt(page);
    const { totalPages } = getPaginatedProducts();
    
    if (num >= 1 && num <= totalPages) {
      setCurrentPage(num);
      setGoToPageInput('');
    }
  };

  // Réinitialiser pagination quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showOnlySelected]);

  const columnOptions = [
    { key: 'code_produit', label: 'Code produit' },
    { key: 'categorie', label: 'Catégorie' },
    { key: 'stock_actuel', label: 'Stock actuel' },
    { key: 'stock_minimum', label: 'Seuil minimum' },
    { key: 'prix_achat', label: 'Prix d\'achat unitaire' },
    { key: 'quantite', label: 'Quantité à commander' },
    { key: 'cout_total', label: 'Coût total' },
    { key: 'checkbox', label: 'Cases à cocher (pour pointer)' }
  ];

  const togglePrintColumn = (key) => {
    setPrintColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Liste de Ravitaillement</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-info { display: flex; gap: 15px; align-items: flex-start; }
            .company-logo { width: 70px; height: 70px; object-fit: contain; border-radius: 8px; }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .company-details { font-size: 12px; color: #6b7280; line-height: 1.6; }
            .doc-title { text-align: right; }
            .doc-title h1 { font-size: 24px; color: #10b981; margin-bottom: 5px; }
            .doc-title .date { font-size: 12px; color: #6b7280; }
            .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-around; text-align: center; }
            .summary-item { }
            .summary-value { font-size: 18px; font-weight: bold; color: #10b981; }
            .summary-label { font-size: 11px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #10b981; color: white; padding: 10px 8px; text-align: left; font-size: 11px; text-transform: uppercase; }
            td { padding: 10px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
            tr:nth-child(even) { background: #f9fafb; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .checkbox-cell { width: 30px; }
            .checkbox { width: 14px; height: 14px; border: 2px solid #10b981; display: inline-block; border-radius: 3px; }
            .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; text-align: center; }
            .total-row { font-weight: bold; background: #d1fae5 !important; }
            .product-name { font-weight: 500; }
            .product-code { font-size: 10px; color: #6b7280; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    setShowPrintModal(false);
  };

  const getProductsToPrint = () => {
    return data?.products?.filter(p => selectedProducts[p.id]?.selected) || [];
  };

  const handleRestock = async () => {
    setProcessing(true);
    try {
      const productsToRestock = data?.products
        ?.filter(p => selectedProducts[p.id]?.selected && selectedProducts[p.id]?.quantity > 0)
        ?.map(p => ({
          product_id: p.id,
          quantity: selectedProducts[p.id].quantity
        }));

      if (productsToRestock.length === 0) {
        alert('Aucun produit sélectionné');
        setProcessing(false);
        return;
      }

      const result = await api.post(`/projects/${projectId}/workspace/restock`, { products: productsToRestock });
      
      setShowConfirm(false);
      loadRestockList();
      
      const ref = result.data?.reference || 'N/A';
      const cout = result.data?.total_cout?.toLocaleString() || '0';
      alert(`✅ Ravitaillement effectué avec succès !\n\n📋 Référence: ${ref}\n💰 Décaissement créé: ${cout} XOF\n\nVous pouvez consulter l'historique pour plus de détails.`);
    } catch (err) {
      console.error('Erreur ravitaillement:', err);
      alert('Erreur lors du ravitaillement');
    } finally {
      setProcessing(false);
    }
  };

  const summary = getSelectedSummary();

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to={`/projects/${projectId}/workspace`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2"><Truck className="w-6 h-6" /> Ravitaillement de stock</h1>
            <p className="text-gray-500 text-sm">Produits à commander pour maintenir le stock au seuil minimum</p>
          </div>
        </div>
        <Link
          to={`/projects/${projectId}/workspace/restock/history`}
          className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 flex items-center gap-2 text-sm"
        >
          <ScrollText className="w-4 h-4" /> Historique
        </Link>
      </div>

      {/* Explication de la liste */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex gap-3">
          <div className="text-2xl">ℹ️</div>
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Comment fonctionne cette liste</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li><strong>✓ Produits critiques</strong> (⚠️ badge) : Stock ≤ seuil minimum → <strong>présélectionnés</strong></li>
              <li><strong>○ Produits normaux</strong> : Stock > seuil → vous pouvez les sélectionner manuellement si vous voulez ravitailler</li>
              <li><strong>Buttons d'action</strong> : "Tout sélectionner", "Tout désélectionner", "○ Tous les produits" / "✓ Sélectionnés"</li>
              <li><strong>Barre de recherche</strong> : Trouvez rapidement un produit par nom, code ou catégorie</li>
              <li><strong>Quantité à commander</strong> : Modifiable pour chaque produit sélectionné</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Résumé */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-4 md:p-6 text-white mb-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl md:text-3xl font-bold">{data?.summary?.total_produits || 0}</div>
            <div className="text-xs md:text-sm opacity-80">Produits à ravitailler</div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-bold">{data?.summary?.total_unites || 0}</div>
            <div className="text-xs md:text-sm opacity-80">Unités nécessaires</div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-bold">{data?.summary?.cout_total?.toLocaleString() || 0}</div>
            <div className="text-xs md:text-sm opacity-80">Coût estimé (XOF)</div>
          </div>
        </div>
      </div>

      {data?.products?.length > 0 ? (
        <>
          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200"
              >
                Tout sélectionner
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Tout désélectionner
              </button>
              <button
                onClick={() => setShowPrintModal(true)}
                disabled={summary.totalProducts === 0}
                className="px-3 py-1.5 text-sm bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 flex items-center gap-1 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" /> Imprimer la liste
              </button>
              <button
                onClick={() => setShowOnlySelected(!showOnlySelected)}
                className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 transition ${
                  showOnlySelected 
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showOnlySelected ? '✓ Sélectionnés' : '○ Tous les produits'}
              </button>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-emerald-600">{summary.totalProducts}</span> produits sélectionnés
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="🔍 Rechercher par nom, code ou catégorie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Liste des produits */}
          <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Produit</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock actuel</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Seuil</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">À commander</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Coût</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getPaginatedProducts().products.map(product => (
                  <tr 
                    key={product.id} 
                    className={`hover:bg-gray-50 transition ${
                      product.is_critical === 1 
                        ? selectedProducts[product.id]?.selected ? 'bg-red-50' : 'bg-red-50/50' 
                        : selectedProducts[product.id]?.selected ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedProducts[product.id]?.selected || false}
                          onChange={() => toggleProduct(product.id)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">{product.nom}</span>
                            {product.is_critical === 1 && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">⚠️ Critique</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{product.code_produit} • {product.categorie}</div>
                        </div>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-semibold ${product.stock_actuel === 0 ? 'text-red-600' : product.is_critical === 1 ? 'text-orange-600' : 'text-green-600'}`}>
                        {product.stock_actuel}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">{product.unite}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {product.stock_minimum}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        value={selectedProducts[product.id]?.quantity || 0}
                        onChange={(e) => updateQuantity(product.id, e.target.value)}
                        disabled={!selectedProducts[product.id]?.selected}
                        className={`w-20 mx-auto block text-center px-2 py-1 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                          !selectedProducts[product.id]?.selected ? 'bg-gray-100 text-gray-400' : 'bg-white'
                        }`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-medium text-gray-800">
                        {((selectedProducts[product.id]?.quantity || 0) * (product.prix_achat || 0)).toLocaleString()} XOF
                      </div>
                      <div className="text-xs text-gray-500">
                        {(product.prix_achat || 0).toLocaleString()} XOF/unité
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Message si aucun résultat de recherche */}
          {getFilteredProducts().length === 0 && searchQuery && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center mb-6">
              <p className="text-amber-800">
                Aucun produit trouvé pour "<strong>{searchQuery}</strong>". Essayez une autre recherche.
              </p>
            </div>
          )}

          {/* Pagination Controls */}
          {(() => {
            const { totalPages, totalItems, products } = getPaginatedProducts();
            return totalPages > 1 ? (
              <div className="bg-white rounded-xl shadow p-4 mb-6">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                  {/* Info */}
                  <div className="text-sm text-gray-600">
                    Affichage <span className="font-semibold">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
                    <span className="font-semibold">{Math.min(currentPage * itemsPerPage, totalItems)}</span> sur{' '}
                    <span className="font-semibold">{totalItems}</span> produits
                  </div>

                  {/* Pagination Buttons */}
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    {/* First */}
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      title="Première page"
                    >
                      ≪
                    </button>
                    {/* Previous */}
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      title="Page précédente"
                    >
                      ‹
                    </button>

                    {/* Page Numbers */}
                    {getPaginationNumbers().map((page, idx) => (
                      <button
                        key={idx}
                        onClick={() => typeof page === 'number' && setCurrentPage(page)}
                        disabled={typeof page !== 'number'}
                        className={`px-2 py-1 rounded-lg text-sm transition ${
                          page === currentPage
                            ? 'bg-emerald-600 text-white'
                            : typeof page === 'number'
                            ? 'border hover:bg-gray-100'
                            : 'border text-gray-400 cursor-default'
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    {/* Next */}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      title="Page suivante"
                    >
                      ›
                    </button>
                    {/* Last */}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      title="Dernière page"
                    >
                      ≫
                    </button>
                  </div>

                  {/* Items Per Page & Go To Page */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Par page:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(parseInt(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 border rounded-lg text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Aller à:</label>
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPageInput}
                        onChange={(e) => setGoToPageInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleGoToPage(goToPageInput)}
                        placeholder={currentPage}
                        className="w-16 px-2 py-1 border rounded-lg text-sm"
                      />
                      <button
                        onClick={() => handleGoToPage(goToPageInput)}
                        className="px-2 py-1 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* Barre de validation fixe */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 md:ml-64">
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-center sm:text-left">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{summary.totalProducts}</span> produits • 
                  <span className="font-semibold ml-1">{summary.totalUnits}</span> unités
                </div>
                <div className="text-lg font-bold text-emerald-600">
                  Total: {summary.totalCost.toLocaleString()} XOF
                </div>
              </div>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={summary.totalProducts === 0}
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Valider le ravitaillement
              </button>
            </div>
          </div>

          <div className="h-24"></div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Stock suffisant !</h3>
          <p className="text-gray-500 mb-4">
            Tous vos produits sont au-dessus du seuil minimum.
          </p>
          <Link
            to={`/projects/${projectId}/workspace/products`}
            className="text-emerald-600 hover:underline"
          >
            Voir tous les produits →
          </Link>
        </div>
      )}

      {/* Modal de confirmation */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Truck className="w-5 h-5" /> Confirmer le ravitaillement</h3>
            <p className="text-gray-600 mb-4">
              Vous allez ajouter au stock :
            </p>
            <div className="bg-emerald-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Produits :</span>
                <span className="font-semibold">{summary.totalProducts}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Unités :</span>
                <span className="font-semibold">{summary.totalUnits}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-600">Coût estimé :</span>
                <span className="font-bold text-emerald-600">{summary.totalCost.toLocaleString()} XOF</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleRestock}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    En cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'options d'impression */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Printer className="w-5 h-5" /> Imprimer la liste de ravitaillement</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Sélectionnez les colonnes à afficher sur la feuille :
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              {columnOptions.map(option => (
                <label 
                  key={option.key}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={printColumns[option.key]}
                    onChange={() => togglePrintColumn(option.key)}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>

            <div className="bg-emerald-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-emerald-700 flex items-center gap-1">
                <ClipboardList className="w-4 h-4" /> <strong>{summary.totalProducts}</strong> produits seront imprimés pour un total de <strong>{summary.totalCost.toLocaleString()} XOF</strong>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPrintModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handlePrint}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" /> Imprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu imprimable (caché) */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {/* En-tête entreprise */}
          <div className="header">
            <div className="company-info">
              {settings?.logo && (
                <img 
                  src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                  alt="Logo"
                  className="company-logo"
                />
              )}
              <div>
                <div className="company-name">{settings?.nom_entreprise || 'Mon Entreprise'}</div>
                <div className="company-details">
                  {settings?.adresse && <div>{settings.adresse}</div>}
                  {settings?.telephone && <div>Tél: {settings.telephone}</div>}
                  {settings?.email && <div>Email: {settings.email}</div>}
                  {settings?.numero_registre && <div>RCCM: {settings.numero_registre}</div>}
                  {settings?.numero_fiscal && <div>NIF: {settings.numero_fiscal}</div>}
                </div>
              </div>
            </div>
            <div className="doc-title">
              <h1>LISTE DE RAVITAILLEMENT</h1>
              <div className="date">Date: {new Date().toLocaleDateString('fr-FR')}</div>
            </div>
          </div>

          {/* Résumé */}
          <div className="summary">
            <div className="summary-item">
              <div className="summary-value">{summary.totalProducts}</div>
              <div className="summary-label">Produits</div>
            </div>
            <div className="summary-item">
              <div className="summary-value">{summary.totalUnits}</div>
              <div className="summary-label">Unités à commander</div>
            </div>
            {printColumns.cout_total && (
              <div className="summary-item">
                <div className="summary-value">{summary.totalCost.toLocaleString()} XOF</div>
                <div className="summary-label">Coût estimé</div>
              </div>
            )}
          </div>

          {/* Tableau des produits */}
          <table>
            <thead>
              <tr>
                {printColumns.checkbox && <th className="checkbox-cell"></th>}
                <th>Produit</th>
                {printColumns.code_produit && <th>Code</th>}
                {printColumns.categorie && <th>Catégorie</th>}
                {printColumns.stock_actuel && <th className="text-center">Stock actuel</th>}
                {printColumns.stock_minimum && <th className="text-center">Seuil</th>}
                {printColumns.quantite && <th className="text-center">Qté à commander</th>}
                {printColumns.prix_achat && <th className="text-right">Prix unitaire</th>}
                {printColumns.cout_total && <th className="text-right">Coût total</th>}
              </tr>
            </thead>
            <tbody>
              {getProductsToPrint().map(product => {
                const qty = selectedProducts[product.id]?.quantity || 0;
                const cost = qty * (product.prix_achat || 0);
                return (
                  <tr key={product.id}>
                    {printColumns.checkbox && (
                      <td className="checkbox-cell">
                        <span className="checkbox"></span>
                      </td>
                    )}
                    <td>
                      <div className="product-name">{product.nom}</div>
                    </td>
                    {printColumns.code_produit && (
                      <td><span className="product-code">{product.code_produit}</span></td>
                    )}
                    {printColumns.categorie && <td>{product.categorie}</td>}
                    {printColumns.stock_actuel && (
                      <td className="text-center">{product.stock_actuel} {product.unite}</td>
                    )}
                    {printColumns.stock_minimum && (
                      <td className="text-center">{product.stock_minimum}</td>
                    )}
                    {printColumns.quantite && (
                      <td className="text-center"><strong>{qty}</strong> {product.unite}</td>
                    )}
                    {printColumns.prix_achat && (
                      <td className="text-right">{(product.prix_achat || 0).toLocaleString()} XOF</td>
                    )}
                    {printColumns.cout_total && (
                      <td className="text-right">{cost.toLocaleString()} XOF</td>
                    )}
                  </tr>
                );
              })}
              {printColumns.cout_total && (
                <tr className="total-row">
                  {printColumns.checkbox && <td></td>}
                  <td colSpan={
                    1 + 
                    (printColumns.code_produit ? 1 : 0) + 
                    (printColumns.categorie ? 1 : 0) + 
                    (printColumns.stock_actuel ? 1 : 0) + 
                    (printColumns.stock_minimum ? 1 : 0) + 
                    (printColumns.quantite ? 1 : 0) + 
                    (printColumns.prix_achat ? 1 : 0)
                  } className="text-right">TOTAL</td>
                  <td className="text-right">{summary.totalCost.toLocaleString()} XOF</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pied de page */}
          <div className="footer">
            <p>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
            {settings?.nom_entreprise && <p>© {new Date().getFullYear()} {settings.nom_entreprise}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectRestock;
